import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem,
    Button, IconButton, Tooltip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableRow,
    Toolbar, TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Divider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";
import EnhancedTableHead from "../../components/EnhancedTableHead";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import ImagePreviewCell from "../../components/ImagePreviewCell";

registerPlugin(FilePondPluginImagePreview);

const headCells = [
    { id: "order", label: "Image ID", disablePadding: false, numeric: false },
    { id: "desktop", label: "Desktop Preview", disablePadding: false, numeric: false },
    { id: "mobile", label: "Mobile Preview", disablePadding: false, numeric: false },
    { id: "orientation", label: "Orientation", disablePadding: false, numeric: false },
    { id: "alt", label: "Alt Text", disablePadding: false, numeric: false },
    { id: "actions", label: "Actions", disablePadding: false, numeric: true },
];

export default function WallpaperPage() {
    const isAdmin = localStorage.getItem("isAdmin") === "true";

    // Categories state
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [categoriesLoading, setCategoriesLoading] = useState(true);

    // Create New Category state
    const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");

    // Add Wallpaper Modal state
    const [addWallpaperOpen, setAddWallpaperOpen] = useState(false);

    // Add Wallpaper state
    const [mobileImageFile, setMobileImageFile] = useState([]);
    const [desktopImageFile, setDesktopImageFile] = useState([]);
    const [mobileImageUrl, setMobileImageUrl] = useState("");
    const [desktopImageUrl, setDesktopImageUrl] = useState("");
    const [orientation, setOrientation] = useState("");
    const [altText, setAltText] = useState("");
    const [saving, setSaving] = useState(false);

    // Wallpapers table state
    const [wallpapers, setWallpapers] = useState([]);
    const [wallpapersLoading, setWallpapersLoading] = useState(false);

    // Delete confirmation dialog
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const res = await api.get("/api/wallpaper/categories");
            const list = res.data.data; // [{ category, count }]
            setCategories(list);
        } catch (err) {
            console.error("Failed to fetch categories:", err);
            showSnackbar("Failed to load categories.", "error");
        } finally {
            setCategoriesLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchWallpapers = useCallback(async (category) => {
        if (!category) {
            setWallpapers([]);
            return;
        }
        setWallpapersLoading(true);
        try {
            const res = await api.get("/api/wallpaper", { params: { category } });
            const list = res.data?.data ?? [];
            setWallpapers(list);
        } catch (err) {
            console.error("Failed to fetch wallpapers:", err);
            showSnackbar("Failed to load wallpapers.", "error");
        } finally {
            setWallpapersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedCategory && selectedCategory !== "CREATE_NEW") {
            fetchWallpapers(selectedCategory);
        } else {
            setWallpapers([]);
        }
    }, [selectedCategory, fetchWallpapers]);

    const handleCategoryChange = (e) => {
        const val = e.target.value;
        if (val === "CREATE_NEW") {
            setCreateCategoryOpen(true);
            setNewCategoryName("");
        } else {
            setSelectedCategory(val);
        }
    };

    const handleCreateCategory = () => {
        const cat = newCategoryName.trim();
        if (!cat) {
            showSnackbar("Category name cannot be empty", "warning");
            return;
        }
        if (categories.find(c => c.category === cat)) {
            showSnackbar("Category already exists", "warning");
            return;
        }
        setCategories([...categories, { category: cat, count: 0 }]);
        setSelectedCategory(cat);
        setCreateCategoryOpen(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedCategory || selectedCategory === "CREATE_NEW") {
            showSnackbar("Please select a valid category.", "warning");
            return;
        }

        const hasMobile = mobileImageFile.length > 0 || mobileImageUrl.trim() !== "";
        const hasDesktop = desktopImageFile.length > 0 || desktopImageUrl.trim() !== "";

        if (!hasMobile && !hasDesktop) {
            showSnackbar("At least one image (mobile or desktop) must be provided.", "warning");
            return;
        }

        setSaving(true);

        const p = new FormData();
        p.append("category", selectedCategory);
        p.append("orientation", orientation);
        p.append("alt", altText);

        if (mobileImageFile.length > 0 && mobileImageFile[0].file) {
            p.append("mobileImageFile", mobileImageFile[0].file);
        } else if (mobileImageUrl) {
            p.append("mobileImage", mobileImageUrl);
        }

        if (desktopImageFile.length > 0 && desktopImageFile[0].file) {
            p.append("desktopImageFile", desktopImageFile[0].file);
        } else if (desktopImageUrl) {
            p.append("desktopImage", desktopImageUrl);
        }

        try {
            await api.post("/api/wallpaper", p);
            showSnackbar("Wallpaper saved successfully!", "success");

            // Reset form
            setMobileImageFile([]);
            setDesktopImageFile([]);
            setMobileImageUrl("");
            setDesktopImageUrl("");
            setOrientation("");
            setAltText("");
            setAddWallpaperOpen(false);

            fetchWallpapers(selectedCategory);
            fetchCategories(); // Update counts
        } catch (err) {
            console.error("Save failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to save wallpaper.", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (wallpaper) => {
        setDeleteTarget(wallpaper);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete("/api/wallpaper", {
                data: { category: deleteTarget.collection, order: deleteTarget.order }
            });
            showSnackbar("Wallpaper deleted successfully.", "success");
            fetchWallpapers(selectedCategory);
        } catch (err) {
            console.error("Delete failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to delete wallpaper.", "error");
        } finally {
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
        }
    };

    return (
        <Box sx={{ width: "100%", px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
            {snackbar.open && (
                <SnackBarComponent
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            )}

            <Paper sx={{ width: "100%", mb: 3 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InsertPhotoIcon color="primary" />
                        <Typography variant="h6" component="div">
                            WALLPAPER MANAGEMENT
                        </Typography>
                    </Box>
                </Toolbar>

                <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel id="category-select-label">Select Category</InputLabel>
                        <Select
                            labelId="category-select-label"
                            id="category-select"
                            value={selectedCategory}
                            label="Select Category"
                            onChange={handleCategoryChange}
                            disabled={categoriesLoading}
                        >
                            {categoriesLoading ? (
                                <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading categories…
                                </MenuItem>
                            ) : (
                                categories.map((c) => (
                                    <MenuItem key={c.category} value={c.category}>
                                        {c.category}
                                    </MenuItem>
                                ))
                            )}
                            <Divider />
                            <MenuItem value="CREATE_NEW" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                <AddIcon fontSize="small" sx={{ mr: 1 }} /> Create New Category
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* Add New Wallpaper Dialog */}
            <Dialog open={addWallpaperOpen} onClose={() => setAddWallpaperOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Add New Wallpaper to "{selectedCategory}"</DialogTitle>
                <DialogContent dividers>
                    <form id="add-wallpaper-form" onSubmit={handleSave}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" display="block" sx={{ mb: 1, color: 'text.secondary' }}>Desktop Image</Typography>
                                <TextField
                                    label="Desktop Image URL"
                                    value={desktopImageUrl}
                                    onChange={(e) => setDesktopImageUrl(e.target.value)}
                                    fullWidth
                                    disabled={desktopImageFile.length > 0}
                                    helperText="Enter URL OR upload a file."
                                    sx={{ mb: 2 }}
                                    size="small"
                                />
                                <FilePond
                                    files={desktopImageFile}
                                    onupdatefiles={setDesktopImageFile}
                                    allowMultiple={false}
                                    acceptedFileTypes={['image/*']}
                                    labelIdle='Drag & Drop your desktop image or <span class="filepond--label-action">Browse</span>'
                                    disabled={desktopImageUrl.trim().length > 0}
                                    credits={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" display="block" sx={{ mb: 1, color: 'text.secondary' }}>Mobile Image</Typography>
                                <TextField
                                    label="Mobile Image URL"
                                    value={mobileImageUrl}
                                    onChange={(e) => setMobileImageUrl(e.target.value)}
                                    fullWidth
                                    disabled={mobileImageFile.length > 0}
                                    helperText="Enter URL OR upload a file."
                                    sx={{ mb: 2 }}
                                    size="small"
                                />
                                <FilePond
                                    files={mobileImageFile}
                                    onupdatefiles={setMobileImageFile}
                                    allowMultiple={false}
                                    acceptedFileTypes={['image/*']}
                                    labelIdle='Drag & Drop your mobile image or <span class="filepond--label-action">Browse</span>'
                                    disabled={mobileImageUrl.trim().length > 0}
                                    credits={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Orientation</InputLabel>
                                    <Select
                                        value={orientation}
                                        label="Orientation"
                                        onChange={(e) => setOrientation(e.target.value)}
                                    >
                                        <MenuItem value="portrait">Portrait</MenuItem>
                                        <MenuItem value="landscape">Landscape</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Alt Text"
                                    value={altText}
                                    onChange={(e) => setAltText(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddWallpaperOpen(false)}>Cancel</Button>
                    <Button
                        type="submit"
                        form="add-wallpaper-form"
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? "Uploading..." : "Add Wallpaper"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* List Wallpapers */}
            <Paper sx={{ width: "100%", mb: 2 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6" component="div">
                        {selectedCategory && selectedCategory !== "CREATE_NEW"
                            ? `Wallpapers in ${selectedCategory}`
                            : "Select a category above to view wallpapers"}
                    </Typography>
                    {selectedCategory && selectedCategory !== "CREATE_NEW" && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setAddWallpaperOpen(true)}
                        >
                            Add Wallpaper
                        </Button>
                    )}
                </Toolbar>

                <TableContainer>
                    <Table sx={{ minWidth: 450 }} aria-label="wallpapers table" size="small">
                        <EnhancedTableHead
                            headCells={headCells}
                            order="desc"
                            orderBy="order"
                            onRequestSort={() => { }}
                            showCheckbox={false}
                            rowCount={wallpapers.length}
                            numSelected={0}
                        />
                        <TableBody>
                            {wallpapersLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size="1.5rem" />
                                    </TableCell>
                                </TableRow>
                            ) : !selectedCategory || selectedCategory === "CREATE_NEW" ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Alert severity="info" sx={{ justifyContent: "center" }}>
                                            Select a category to view its wallpapers.
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            ) : wallpapers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Alert severity="info" sx={{ justifyContent: "center" }}>
                                            No wallpapers found in this category.
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                wallpapers.map((w) => (
                                    <TableRow
                                        key={w.order}
                                        hover
                                    >
                                        <TableCell>{w.order}</TableCell>
                                        <TableCell>
                                            {w.image?.desktop ? (
                                                <ImagePreviewCell imageUrl={w.image.desktop} alt="Desktop Image" />
                                            ) : "-"}
                                        </TableCell>
                                        <TableCell>
                                            {w.image?.mobile ? (
                                                <ImagePreviewCell imageUrl={w.image.mobile} alt="Mobile Image" />
                                            ) : "-"}
                                        </TableCell>
                                        <TableCell sx={{ textTransform: "capitalize" }}>{w.orientation || "-"}</TableCell>
                                        <TableCell>{w.alt || "-"}</TableCell>
                                        <TableCell align="right">
                                            {isAdmin ? (
                                                <Tooltip title="Delete wallpaper">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(w)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="You do not have permission to delete this wallpaper.">
                                                    <IconButton size="small" color="error" disabled>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Create Category Dialog */}
            <Dialog open={createCategoryOpen} onClose={() => setCreateCategoryOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Create New Category</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Category Name"
                        fullWidth
                        variant="outlined"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateCategory();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateCategoryOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateCategory} variant="contained" color="primary">Create</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                selectedCount={deleteTarget ? 1 : 0}
                onCancel={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                itemLabel="wallpaper"
            />
        </Box>
    );
}